import { useState } from 'react'
import { ApiError } from '../api/client'
import { useDeleteGuest, useGuests, useRenameGuest } from '../api/hooks'
import type { Guest } from '../api/types'
import { MeepleAvatar, ScorePad, ScorePadRow, SectionHeader } from '../primitives'

export type GuestsSectionProps = {
  // null = personal guests (visible only to the creator)
  // string = guests scoped to that group (visible to all active members)
  groupId: string | null
}

export const GuestsSection = ({ groupId }: GuestsSectionProps) => {
  const guests = useGuests()
  const visible = (guests.data ?? []).filter((g) =>
    groupId === null ? g.groupId === null : g.groupId === groupId,
  )

  const kicker = groupId === null ? 'personal' : 'group'
  const emptyText =
    groupId === null
      ? 'No personal guests yet. Add one from the Players step when recording a no-group play.'
      : 'No group guests yet. Add one from the Players step when recording a play in this group.'

  return (
    <>
      <SectionHeader kicker={kicker} title="Guests" />
      <div style={{ padding: '0 16px' }}>
        {guests.isLoading ? (
          <div style={{ fontSize: 12, color: 'var(--ink-3)' }}>Loading…</div>
        ) : visible.length === 0 ? (
          <div className="felt-card" style={{ padding: 12, fontSize: 12, color: 'var(--ink-2)' }}>
            {emptyText}
          </div>
        ) : (
          <ScorePad>
            {visible.map((g) => (
              <GuestRow key={g.id} guest={g} />
            ))}
          </ScorePad>
        )}
      </div>
    </>
  )
}

type RowMode = 'view' | 'rename' | 'confirm-delete'

const GuestRow = ({ guest }: { guest: Guest }) => {
  const [mode, setMode] = useState<RowMode>('view')
  const [draftName, setDraftName] = useState(guest.name)
  const [error, setError] = useState<string | null>(null)
  const rename = useRenameGuest()
  const del = useDeleteGuest()

  const startRename = () => {
    setDraftName(guest.name)
    setError(null)
    setMode('rename')
  }
  const cancel = () => {
    setError(null)
    setMode('view')
  }

  const submitRename = async () => {
    const name = draftName.trim()
    if (!name || name === guest.name) {
      cancel()
      return
    }
    setError(null)
    try {
      await rename.mutateAsync({ id: guest.id, name })
      setMode('view')
    } catch (err) {
      setError(err instanceof ApiError ? err.friendlyMessage : 'Rename failed.')
    }
  }

  const submitDelete = async () => {
    setError(null)
    try {
      await del.mutateAsync(guest.id)
      // Row vanishes from the list because useGuests re-fetches.
    } catch (err) {
      setError(err instanceof ApiError ? err.friendlyMessage : 'Delete failed.')
      setMode('view')
    }
  }

  if (mode === 'rename') {
    return (
      <ScorePadRow>
        <MeepleAvatar name={draftName || guest.name} hue={0} shape="meeple" size={36} ghost />
        <input
          type="text"
          autoFocus
          value={draftName}
          onChange={(ev) => setDraftName(ev.target.value)}
          onKeyDown={(ev) => {
            if (ev.key === 'Enter') {
              ev.preventDefault()
              submitRename()
            } else if (ev.key === 'Escape') {
              ev.preventDefault()
              cancel()
            }
          }}
          maxLength={40}
          style={{
            flex: 1,
            fontSize: 14,
            padding: '6px 8px',
            border: '1px solid var(--line)',
            borderRadius: 4,
            background: 'var(--paper)',
            fontFamily: 'inherit',
          }}
        />
        <button
          type="button"
          onClick={submitRename}
          disabled={rename.isPending || !draftName.trim()}
          className="btn-stamp"
          style={{ fontSize: 12, padding: '6px 10px' }}
        >
          {rename.isPending ? '…' : 'Save'}
        </button>
        <button
          type="button"
          onClick={cancel}
          disabled={rename.isPending}
          className="btn-ghost"
          style={{ fontSize: 12, padding: '6px 8px' }}
        >
          Cancel
        </button>
      </ScorePadRow>
    )
  }

  if (mode === 'confirm-delete') {
    return (
      <ScorePadRow>
        <MeepleAvatar name={guest.name} hue={0} shape="meeple" size={36} ghost />
        <div style={{ flex: 1, fontSize: 13, color: 'var(--ink-2)' }}>
          Delete <strong>{guest.name}</strong>? Past plays keep the name.
        </div>
        <button
          type="button"
          onClick={submitDelete}
          disabled={del.isPending}
          className="btn-stamp"
          style={{ fontSize: 12, padding: '6px 10px', background: 'var(--terra-ink)' }}
        >
          {del.isPending ? '…' : 'Delete'}
        </button>
        <button
          type="button"
          onClick={cancel}
          disabled={del.isPending}
          className="btn-ghost"
          style={{ fontSize: 12, padding: '6px 8px' }}
        >
          Cancel
        </button>
      </ScorePadRow>
    )
  }

  return (
    <ScorePadRow>
      <MeepleAvatar name={guest.name} hue={0} shape="meeple" size={36} ghost />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 600, fontSize: 14 }}>{guest.name}</div>
        {error && <div style={{ fontSize: 11, color: 'var(--terra-ink)' }}>{error}</div>}
      </div>
      <button
        type="button"
        onClick={startRename}
        className="btn-ghost"
        style={{ fontSize: 12, padding: '6px 8px' }}
      >
        Rename
      </button>
      <button
        type="button"
        onClick={() => setMode('confirm-delete')}
        className="btn-ghost"
        style={{ fontSize: 12, padding: '6px 8px', color: 'var(--terra-ink)' }}
      >
        Delete
      </button>
    </ScorePadRow>
  )
}
